import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Briefcase,
  Clock,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(false);
    const load = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${slug}`);
        if (!cancelled) setJob(res.data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const applySubject = job
    ? `Application — ${job.title}`
    : `Application — ${slug}`;
  const applyHref = `mailto:careers@t3partner.in?subject=${encodeURIComponent(
    applySubject,
  )}`;

  return (
    <div data-testid="job-detail-page">
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24">
        <Link
          to="/careers"
          data-testid="job-detail-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 transition-colors mb-8"
        >
          <ArrowLeft size={14} /> Back to all roles
        </Link>

        {loading && (
          <div
            data-testid="job-detail-loading"
            className="flex items-center gap-3 text-slate-500 text-sm"
          >
            <Loader2 size={16} className="animate-spin" /> Loading role…
          </div>
        )}

        {!loading && notFound && (
          <div
            data-testid="job-detail-notfound"
            className="border border-slate-200 rounded-xl p-10 bg-white text-center"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 text-slate-500 mb-5">
              <AlertCircle size={26} />
            </div>
            <h2 className="font-heading text-2xl font-medium text-slate-900">
              This role isn't open.
            </h2>
            <p className="text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
              The listing you're looking for is no longer available. Head back to see what else is open, or send us your CV directly.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                variant="outline"
                className="border-slate-300 text-slate-900 hover:bg-slate-50 rounded-md h-11 px-5"
              >
                <Link to="/careers">See all roles</Link>
              </Button>
              <Button
                asChild
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-11 px-5"
              >
                <a href="mailto:careers@t3partner.in">
                  <Mail className="mr-2" size={16} /> Email us your CV
                </a>
              </Button>
            </div>
          </div>
        )}

        {!loading && error && !notFound && (
          <div
            data-testid="job-detail-error"
            className="border border-slate-200 rounded-xl p-8 bg-white"
          >
            <p className="text-slate-600 text-sm">
              We couldn't load this role right now. Please refresh, or email us at{" "}
              <a
                href="mailto:careers@t3partner.in"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                careers@t3partner.in
              </a>
              .
            </p>
          </div>
        )}

        {!loading && job && (
          <>
            <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-4">
              Open role
            </div>
            <h1
              data-testid="job-detail-title"
              className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-slate-900 leading-[1.1]"
            >
              {job.title}
            </h1>

            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              {job.department && (
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase size={14} /> {job.department}
                </span>
              )}
              {job.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} /> {job.location}
                </span>
              )}
              {job.type && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} /> {job.type}
                </span>
              )}
              {job.experience && (
                <span className="text-slate-600">{job.experience}</span>
              )}
              {job.posted && (
                <span className="text-slate-500 uppercase text-xs tracking-wider">
                  Posted {job.posted}
                </span>
              )}
            </div>

            <div className="mt-10 border-t border-slate-200 pt-10 job-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {job.body_md || ""}
              </ReactMarkdown>
            </div>

            <div className="mt-12 border border-slate-200 rounded-xl p-8 bg-slate-50">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-2">
                Interested?
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Apply via email — attach your CV and we'll be in touch.
              </p>
              <a
                href={applyHref}
                data-testid="job-detail-apply-email"
                className="mt-3 inline-flex items-center gap-2 font-heading text-lg sm:text-xl font-medium text-slate-900 hover:text-orange-600 transition-colors break-all"
              >
                <Mail size={18} /> careers@t3partner.in
              </a>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
